/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React, { FormEvent } from "react";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";

import Button from "@src/components/ui/Button";
import LoginOptions from "@src/components/modules/LoginModule/LoginOptions";
import LoadingButton from "@src/components/ui/LoadingButton";
import LoginFormField from "@src/components/modules/LoginModule/LoginFormField";

import { loginButtons } from "@src/constants";
import notificationStore from "@src/stores/NotificationStore";
import { ThemePalette } from "@src/components/Theme";
import errorIcon from "./images/error.svg";
import { ssoOptions } from "@src/sources/SsoSource";

// Sign-in from Tech9IQ (docs/swift-sso.md): a link, not a form button, so it never submits the form.
const Tech9iqButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${ThemePalette.primary};
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease;
  &:hover {
    background: ${ThemePalette.primaryLight};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemePalette.accent};
  }
`;

const Form = styled.form`
  width: 100%;
  box-sizing: border-box;
`;

const FormFields = styled.div<any>`
  display: flex;
  gap: 16px;
  ${loginButtons.length < 3
    ? css`
        flex-direction: column;
      `
    : ""}
`;

// Was faded (opacity .5) between two 19px stubs; now a hairline across the card, as signin.tech9iq.com's dividers.
const LoginSeparator = styled.div<any>`
  margin: 4px 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const SeparatorLine = styled.div<any>`
  flex: 1;
  border-top: 1px solid ${ThemePalette.grayscale[0]};
`;

const SeparatorText = styled.div<any>`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  text-align: center;
  white-space: nowrap;
`;
const LoginError = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 16px;
`;

const LoginErrorIcon = styled.div<any>`
  width: 26px;
  height: 26px;
  background-image: url("${errorIcon}");
`;

const LoginErrorText = styled.div<any>`
  color: ${ThemePalette.alert};
  font-size: 13px;
  font-weight: 500;
  margin-top: 6px;
  text-align: center;
`;

type Props = {
  className: string;
  showUserDomainInput: boolean;
  loading: boolean;
  loginFailedResponse: { status: string | number; message?: string } | null;
  domain: string;
  onDomainChange: (domain: string) => void;
  onFormSubmit: (credentials: { username: string; password: string }) => void;
};
type State = {
  username: string;
  password: string;
};
@observer
class LoginForm extends React.Component<Props, State> {
  static defaultProps = {
    className: "",
  };

  state = {
    username: "",
    password: "",
  };

  handleUsernameChange(username: string) {
    this.setState({ username });
  }

  handlePasswordChange(password: string) {
    this.setState({ password });
  }

  handleDomainChange(domain: string) {
    this.props.onDomainChange(domain);
  }

  handleFormSubmit(e: FormEvent) {
    e.preventDefault();

    if (
      !this.state.username.length ||
      !this.state.password.length ||
      !this.props.domain.length
    ) {
      notificationStore.alert("Please fill in all fields");
    } else {
      this.props.onFormSubmit({
        username: this.state.username,
        password: this.state.password,
      });
    }
  }

  renderErrorMessage() {
    if (!this.props.loginFailedResponse) {
      return null;
    }

    let errorMessage =
      "Request failed, there might be a problem with the connection to the server.";

    if (this.props.loginFailedResponse.status) {
      switch (this.props.loginFailedResponse.status) {
        case 401:
          errorMessage = "Incorrect credentials.<br />Please try again.";
          break;
        default:
          errorMessage = this.props.loginFailedResponse.message || errorMessage;
      }
    }

    return (
      <LoginError>
        <LoginErrorIcon />
        <LoginErrorText dangerouslySetInnerHTML={{ __html: errorMessage }} />
      </LoginError>
    );
  }

  render() {
    const sso = ssoOptions();
    // Between Sign in with Tech9IQ (or the other sign-in buttons) and Swift's own form, which comes second.
    // const loginSeparator = loginButtons.length ? ( -- now also below the Tech9IQ button
    const loginSeparator = loginButtons.length || sso.enabled ? (
      <LoginSeparator>
        <SeparatorLine />
        <SeparatorText>{sso.enabled ? "or use a SwiftIQ account" : "or sign in with username"}</SeparatorText>
        <SeparatorLine />
      </LoginSeparator>
    ) : null;

    // const buttonStyle = { width: "100%", marginTop: "24px", height: "40px" }; -- Button's own min/max sizes won over these
    // Full width and 40px, as signin.tech9iq.com's buttons.
    const buttonStyle = { width: "100%", minWidth: "100%", maxWidth: "100%", marginTop: "24px", height: "40px", minHeight: "40px", maxHeight: "40px", borderRadius: "8px", fontWeight: 500 };
    const button = this.props.loading ? (
      <LoadingButton style={buttonStyle}>Please wait ... </LoadingButton>
    ) : (
      // The secondary action beside Sign in with Tech9IQ; the only one (primary) while that isn't set up.
      <Button style={buttonStyle} secondary={sso.enabled}>
        Sign in
      </Button>
    );

    const tech9iqButton = sso.enabled ? (
      <Tech9iqButton href={sso.startUrl} data-test-id="loginForm-tech9iq">
        Sign in with Tech9IQ
      </Tech9iqButton>
    ) : null;
    // SWIFT_LOCAL_LOGIN=false: Tech9IQ is the only way in.
    if (sso.enabled && !sso.localLogin) {
      return (
        <Form className={this.props.className} onSubmit={e => e.preventDefault()}>
          {this.renderErrorMessage()}
          {tech9iqButton}
        </Form>
      );
    }

    return (
      <Form
        className={this.props.className}
        onSubmit={e => {
          this.handleFormSubmit(e);
        }}
      >
        {this.renderErrorMessage()}
        {tech9iqButton}
        <LoginOptions />
        {loginSeparator}
        <FormFields>
          {this.props.showUserDomainInput ? (
            <LoginFormField
              label="Domain"
              value={this.props.domain}
              onChange={e => {
                this.handleDomainChange(e.target.value);
              }}
            />
          ) : null}
          <LoginFormField
            label="Username"
            value={this.state.username}
            name="username"
            onChange={e => {
              this.handleUsernameChange(e.target.value);
            }}
          />
          <LoginFormField
            label="Password"
            value={this.state.password}
            onChange={e => {
              this.handlePasswordChange(e.target.value);
            }}
            name="password"
            type="password"
          />
        </FormFields>
        {button}
      </Form>
    );
  }
}

export default LoginForm;
